import createAdjDetailEntry from './createAdjDetailEntry'
import getSkillFormulaCharacteristics from './getSkillFormulaCharacteristics'
import isReadonlyOverloadingAdj from './isReadonlyOverloadingAdj'

import processCalcTotalsRebuildReasonAppliedAdjsHarness from '../processes/processCalcTotalsRebuildReasonAppliedAdjsHarness'
import processResetTotalsReasonAdjsHarness from '../processes/processResetTotalsReasonAdjsHarness'

function getHalfMoveAdj(currentMove) {
  const moveValue = Math.max(Number(currentMove) || 0, 0)
  return -(moveValue / 2)
}

function buildExistingAdjMap(adjs = []) {
  return new Map(
    adjs.map((adj) => [
      [adj.name || '', adj.adj_reason || '', adj.adj_value ?? 0, adj.applied === true].join('|'),
      adj,
    ]),
  )
}

function buildOverloadingAdjEntry({
  category,
  name,
  adjValue,
  existingAdjsByKey,
  applied = false,
}) {
  const adjReason = 'Overloaded'
  const existingAdj = existingAdjsByKey.get(
    [name || '', adjReason, adjValue ?? 0, applied === true].join('|'),
  )

  return createAdjDetailEntry({
    id: existingAdj?.id ?? null,
    name,
    adj_reason: adjReason,
    adj_value: adjValue,
    character_item: null,
    editable: false,
    rune: '',
    source: category === 'skill' ? 'character' : category,
    applied,
  })
}

function resetAndRebuildRemovedAdjs({ category, removedAdjs, character }) {
  if (!Array.isArray(removedAdjs) || removedAdjs.length === 0) {
    return character
  }

  character = processResetTotalsReasonAdjsHarness({
    adjs: removedAdjs,
    category,
    character,
  })

  return processCalcTotalsRebuildReasonAppliedAdjsHarness({
    adjs: removedAdjs,
    category,
    character,
    add_only: false,
  })
}

function rebuildAddedAdjs({ category, addedAdjs, character }) {
  if (!Array.isArray(addedAdjs) || addedAdjs.length === 0) {
    return character
  }

  return processCalcTotalsRebuildReasonAppliedAdjsHarness({
    adjs: addedAdjs,
    category,
    character,
    add_only: false,
  })
}

function getOverloadedSkillNames(character) {
  return [
    ...new Set(
      (character.skills || [])
        .filter((skill) => {
          if (!skill || typeof skill !== 'object') {
            return false
          }

          const formulaChars = getSkillFormulaCharacteristics(skill).map((entry) =>
            String(entry || '').toLowerCase(),
          )

          return formulaChars.includes('str') || formulaChars.includes('dex')
        })
        .map((skill) => skill?.name)
        .filter(Boolean),
    ),
  ]
}

export default function syncCharacterOverloadingAdjustments(character, overloadingDetails = {}) {
  if (!character || typeof character !== 'object') {
    return character
  }

  character.ability_adjs = Array.isArray(character.ability_adjs) ? character.ability_adjs : []
  character.skills = Array.isArray(character.skills) ? character.skills : []
  character.skill_adjs = Array.isArray(character.skill_adjs) ? character.skill_adjs : []

  const removedAbilityAdjs = character.ability_adjs.filter((adj) =>
    isReadonlyOverloadingAdj(adj?.adj_reason),
  )
  const removedSkillAdjs = character.skill_adjs.filter((adj) =>
    isReadonlyOverloadingAdj(adj?.adj_reason),
  )

  const abilityAdjMap = buildExistingAdjMap(removedAbilityAdjs)
  const skillAdjMap = buildExistingAdjMap(removedSkillAdjs)

  character.ability_adjs = character.ability_adjs.filter(
    (adj) => !isReadonlyOverloadingAdj(adj?.adj_reason),
  )
  character.skill_adjs = character.skill_adjs.filter(
    (adj) => !isReadonlyOverloadingAdj(adj?.adj_reason),
  )

  character = resetAndRebuildRemovedAdjs({
    category: 'ability',
    removedAdjs: removedAbilityAdjs,
    character,
  })
  character = resetAndRebuildRemovedAdjs({
    category: 'skill',
    removedAdjs: removedSkillAdjs,
    character,
  })

  const addedAbilityAdjs = []
  const addedSkillAdjs = []

  const moveAdjRaw = Number(overloadingDetails?.adj_move) || 0
  let moveAdj = moveAdjRaw
  let moveFlyAdj = moveAdjRaw

  if (moveAdjRaw === -99) {
    moveAdj = getHalfMoveAdj(character.move_cur)
    moveFlyAdj = getHalfMoveAdj(character.move_fly_cur)
  }

  if (moveAdj !== 0) {
    const entry = buildOverloadingAdjEntry({
      category: 'ability',
      name: 'move',
      adjValue: moveAdj,
      existingAdjsByKey: abilityAdjMap,
    })
    character.ability_adjs.push(entry)
    addedAbilityAdjs.push(entry)
  }

  if (moveFlyAdj !== 0) {
    const entry = buildOverloadingAdjEntry({
      category: 'ability',
      name: 'move_fly',
      adjValue: moveFlyAdj,
      existingAdjsByKey: abilityAdjMap,
    })
    character.ability_adjs.push(entry)
    addedAbilityAdjs.push(entry)
  }

  const skillAdj = Number(overloadingDetails?.adj_skills) || 0
  if (skillAdj !== 0) {
    getOverloadedSkillNames(character).forEach((skillName) => {
      const entry = buildOverloadingAdjEntry({
        category: 'skill',
        name: skillName,
        adjValue: skillAdj,
        existingAdjsByKey: skillAdjMap,
        applied: true,
      })
      character.skill_adjs.push(entry)
      addedSkillAdjs.push(entry)
    })
  }

  character = rebuildAddedAdjs({
    category: 'ability',
    addedAdjs: addedAbilityAdjs,
    character,
  })
  character = rebuildAddedAdjs({
    category: 'skill',
    addedAdjs: addedSkillAdjs,
    character,
  })

  return character
}
